import React, { useCallback } from "react";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import BatchType from "../../../types/Batch";
import ProgressBar from "../../batches/components/ProgressBar";
import { analyseTheme } from "../../analyse/analyseTheme";
import withContext from "../../../tools/withContext";
import { getHistory } from "../../../tools/batchHistory";
import { loadResumeBatch } from "../hooks/batchQueries";
import { useWidgetAsyncQuery } from "../hooks/useWidgetQuery";
import { panelCardStyles } from "./sharedStyles";
import { canLabelizeBatch, getLabelerPath } from "./utils";
import { ResumeBatchWidgetSkeleton } from "./WidgetSkeletons";

const EMPTY_RESUME: BatchType | null = null;

const styles = (theme: Theme) =>
  createStyles({
    ...panelCardStyles(theme),
    resumeCardSquare: {
      width: 280,
      minWidth: 280,
      aspectRatio: "1",
      flexShrink: 0,
      borderLeft: `4px solid ${theme.palette.secondary.light}`,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        minWidth: 0,
        aspectRatio: "auto",
        minHeight: 220,
      },
    },
    batchName: {
      fontWeight: 600,
      fontSize: "1rem",
      color: analyseTheme.textPrimary,
    },
    batchProject: {
      color: analyseTheme.textSecondary,
      fontSize: "0.8rem",
      fontStyle: "italic",
    },
    batchProgress: {
      width: "100%",
      flexShrink: 0,
      margin: "0 8px",
      "& > div": {
        padding: "0 !important",
      },
    },
    resumeButton: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      marginTop: theme.spacing.unit * 2,
      padding: "8px 16px",
      borderRadius: analyseTheme.radiusSm,
      backgroundColor: theme.palette.secondary.main,
      color: "#fff",
      textDecoration: "none",
      fontWeight: 600,
      fontSize: "0.875rem",
      transition: "background-color 0.2s",
      "&:hover": {
        backgroundColor: theme.palette.secondary.dark,
        color: "#fff",
      },
    },
  });

interface Props {
  classes: Record<string, string>;
  resumeRefreshKey: number;
  onBatchNavigate: (batchId: number) => void;
  ctx: { appFetch: (url: string) => Promise<any>; user: { id: number } };
}

const ResumeBatchWidget = ({
  classes,
  resumeRefreshKey,
  onBatchNavigate,
  ctx: { appFetch, user },
}: Props) => {
  const userId = user?.id;
  const lastHistoryId =
    userId != null
      ? String(getHistory(String(userId)).slice(-1)[0] ?? "none")
      : null;
  const queryKey =
    userId != null
      ? `resume:${userId}:${lastHistoryId}:${resumeRefreshKey}`
      : null;

  const loadData = useCallback(
    () => loadResumeBatch(userId!, appFetch, getHistory),
    [userId, appFetch],
  );

  const { isLoading, data: resumeBatch } = useWidgetAsyncQuery(
    queryKey,
    loadData,
    EMPTY_RESUME,
  );

  if (isLoading) {
    return <ResumeBatchWidgetSkeleton />;
  }

  const userRole = (user as { authorities?: string[] })?.authorities?.[0];
  const showResume = resumeBatch && canLabelizeBatch(resumeBatch, userRole);

  return (
    <Paper
      elevation={0}
      className={`${classes.panelCard} ${classes.resumeCardSquare}`}
    >
      <Typography className={classes.sectionTitle} variant="h6">
        Reprendre le dernier lot
      </Typography>
      {showResume && resumeBatch ? (
        <>
          <Typography className={classes.batchName} noWrap>
            {resumeBatch.name}
          </Typography>
          <Typography className={classes.batchProject} noWrap>
            {resumeBatch.project?.name}
          </Typography>
          <div className={classes.batchProgress} style={{ marginTop: 8 }}>
            <ProgressBar
              totalLabelDocs={resumeBatch.totalLabelDocs}
              totalLabelDocsByModel={resumeBatch.totalLabelDocsByModel}
              totalEligibleDocs={resumeBatch.totalEligibleDocs}
              isVideo={
                resumeBatch.project?.labelingTypes?.[0]?.name === "VIDEO"
              }
            />
          </div>
          <div style={{ flexGrow: 1 }} />
          <a
            href={getLabelerPath(resumeBatch)}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.resumeButton}
            onMouseDown={() => onBatchNavigate(resumeBatch.id)}
          >
            <PlayArrowIcon fontSize="small" />
            Continuer
          </a>
        </>
      ) : (
        <Typography className={classes.panelEmpty}>
          Aucun lot à reprendre.
        </Typography>
      )}
    </Paper>
  );
};

export default withContext(withStyles(styles)(ResumeBatchWidget));
